import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PrimaryTabNavigation from 'components/ui/PrimaryTabNavigation';
import ContentStateIndicator from 'components/ui/ContentStateIndicator';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import ModeSelector from './components/ModeSelector';
import PromptInput from './components/PromptInput';
import GenerationSettings from './components/GenerationSettings';
import GenerationProgress from './components/GenerationProgress';
import GamesLibrary from './components/GamesLibrary';
import PublishModal from './components/PublishModal';
import ShareModal from './components/ShareModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import MusicUpload from './components/MusicUpload';
import OutputPreviewPanel from './components/OutputPreviewPanel';
import TemplateSystem from './components/TemplateSystem';
import AICreatorAssistant from './components/AICreatorAssistant';
import AIAssetGenerator from './components/AIAssetGenerator';
import StoryMultiOutput from './components/StoryMultiOutput';
import CreatorAchievements from './components/CreatorAchievements';
import AutoTrailerGenerator from './components/AutoTrailerGenerator';
import OneClickPublish from './components/OneClickPublish';
import UpgradeModal from './components/UpgradeModal';
import VideoEditor from './components/VideoEditor';
import MonetizationPanel from './components/MonetizationPanel';
import ImageUploadWithSafety from './components/ImageUploadWithSafety';
import TextToMusicGenerator from './components/TextToMusicGenerator';
import SubscriptionTierBadge from './components/SubscriptionTierBadge';
import CreatorAnalytics from './components/CreatorAnalytics';
import LipSyncWorkspace from './components/LipSyncWorkspace';
import ImageToVideoWorkspace from './components/ImageToVideoWorkspace';
import PromptEnhancer from './components/PromptEnhancer';
import PostGenerationSuggestions from './components/PostGenerationSuggestions';
import ToolGallery from './components/ToolGallery';
import MyCreationsTab from './components/MyCreationsTab';
import { useAuth } from '../../contexts/AuthContext';

import { getUserSubscription } from '../../services/billingService';
import ComputeWalletWidget from './components/ComputeWalletWidget';



const checkGameQuota = async (userId, tier, email) => {
  // Simple quota check implementation
  // This should be replaced with actual API call if available
  const quotas = {
    free: 5,
    basic: 20,
    pro: 100,
    enterprise: Infinity
  };

  const limit = quotas?.[tier] || quotas?.free;

  return {
    allowed: true, // In production, check actual usage against limit
    message: limit === Infinity ? 'Unlimited games' : `${limit} games per month`,
    remaining: limit
  };
};

const BANNED_KEYWORDS = ['violence', 'gore', 'sexual', 'nude', 'naked', 'kill', 'murder', 'abuse', 'hate', 'racist', 'drugs'];

const checkSafety = (text) => {
  const lower = text?.toLowerCase();
  return !BANNED_KEYWORDS?.some((kw) => lower?.includes(kw));
};

const STORAGE_KEY = 'gameforge_games';

const INITIAL_GAMES = [
{
  id: 1,
  title: "Crystal Collector",
  prompt: "A platformer where a robot collects energy crystals across 3 levels with increasing difficulty",
  mode: "text-to-game",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1d013c3b9-1773033727743.png",
  thumbnailAlt: "Colorful pixel art platformer game scene with glowing crystals and robot character on floating platforms",
  createdAt: "03/07/2026",
  views: 1240,
  published: true,
  rating: 'E',
  description: 'Collect all crystals to win!'
},
{
  id: 2,
  title: "Star Tap Frenzy",
  prompt: "A quick reaction game where you tap falling stars before they disappear from the screen",
  mode: "prompt-to-mini-game",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1d53f6229-1773033729820.png",
  thumbnailAlt: "Bright yellow stars falling against dark night sky background in arcade style game interface",
  createdAt: "03/05/2026",
  views: 876,
  published: false,
  rating: 'E',
  description: ''
},
{
  id: 3,
  title: "Dragon Flight",
  prompt: "A dragon flying through clouds with fire breath animation and dynamic wind effects",
  mode: "text-to-animation",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_179f29d40-1773062403774.png",
  thumbnailAlt: "Majestic green dragon soaring through dramatic orange and purple sunset clouds with fire breath",
  createdAt: "03/03/2026",
  views: 2103,
  published: true,
  rating: 'E',
  description: 'Watch the dragon soar!'
},
{
  id: 4,
  title: "Space Odyssey Intro",
  prompt: "Opening cinematic: Spaceship lands on alien planet. Crew steps out cautiously into purple atmosphere.",
  mode: "script-to-video",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_188008d6f-1773033732458.png",
  thumbnailAlt: "Silver futuristic spaceship landing on alien planet with purple sky and strange rock formations",
  createdAt: "02/28/2026",
  views: 654,
  published: false,
  rating: 'E',
  description: ''
},
{
  id: 5,
  title: "Neon City Beats",
  prompt: "Upbeat chiptune track with pixel art characters dancing in a neon city at night",
  mode: "music-to-video",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a938869b-1773036689524.png",
  thumbnailAlt: "Vibrant neon city skyline at night with colorful pixel art characters dancing on illuminated streets",
  createdAt: "02/25/2026",
  views: 3421,
  published: true,
  rating: 'E',
  description: 'Dance to the beat!'
},
{
  id: 6,
  title: "Memory Match Animals",
  prompt: "A memory card matching game with cute animal characters and cheerful sound effects",
  mode: "prompt-to-mini-game",
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1880b7098-1772441677699.png",
  thumbnailAlt: "Colorful memory card game grid with cute cartoon animal illustrations on pastel colored cards",
  createdAt: "02/20/2026",
  views: 987,
  published: true,
  rating: 'E',
  description: 'Match all the animals!'
}];


const GAME_THUMBNAILS = [
"https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=600&q=80",
"https://images.unsplash.com/photo-1511512578047-dfb367046420?w=600&q=80",
"https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?w=600&q=80",
"https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=600&q=80"];


function loadGames() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return INITIAL_GAMES;
}

function saveGames(games) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(games));
  } catch {}
}

function StatsBar({ games }) {
  const published = games?.filter((g) => g?.published)?.length;
  const totalViews = games?.reduce((acc, g) => acc + (g?.views || 0), 0);
  const thisMonth = games?.filter((g) => {
    const d = new Date(g?.createdAt);
    const now = new Date();
    return d?.getMonth() === now?.getMonth() && d?.getFullYear() === now?.getFullYear();
  })?.length;

  const stats = [
  { label: 'Total Creations', value: games?.length, icon: 'Layers', color: 'var(--color-primary)' },
  { label: 'Published', value: published, icon: 'Globe', color: 'var(--color-success)' },
  { label: 'Total Views', value: totalViews?.toLocaleString(), icon: 'Eye', color: 'var(--color-secondary)' },
  { label: 'This Month', value: thisMonth, icon: 'Calendar', color: 'var(--color-accent)' }];


  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {stats?.map((s) =>
      <div
        key={s?.label}
        className="flex items-center gap-3 p-3 md:p-4 rounded-xl"
        style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)' }}>
        
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${s?.color}18` }}>
            <Icon name={s?.icon} size={18} color={s?.color} />
          </div>
          <div className="min-w-0">
            <p className="text-lg md:text-xl font-bold leading-tight" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
              {s?.value}
            </p>
            <p className="text-xs truncate" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
              {s?.label}
            </p>
          </div>
        </div>
      )}
    </div>);

}

export default function CreatorDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  // Handle remix from discover page
  const remixItem = location?.state?.remixItem;

  const [mode, setMode] = useState(location?.state?.mode || 'text-to-game');
  const [prompt, setPrompt] = useState(remixItem?.title ? `Remix of: ${remixItem?.title} — ` : '');
  const [complexity, setComplexity] = useState('medium');
  const [safeMode, setSafeMode] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [genStatus, setGenStatus] = useState('idle');
  const [genError, setGenError] = useState(null);
  const [showToolGallery, setShowToolGallery] = useState(false);

  // Subscription tier
  const [userTier, setUserTier] = useState('free');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [testMode, setTestMode] = useState(false);

  // Video editor
  const [showVideoEditor, setShowVideoEditor] = useState(false);
  const [editingVideoGame, setEditingVideoGame] = useState(null);

  // Monetization
  const [monetizeGame, setMonetizeGame] = useState(null);

  // Image upload
  const [uploadedImage, setUploadedImage] = useState(null);

  // Music to Video state
  const [musicFile, setMusicFile] = useState(null);
  const [musicSyncMode, setMusicSyncMode] = useState('beats');
  const [musicOutputType, setMusicOutputType] = useState('full-video');
  const [lyricsText, setLyricsText] = useState('');
  const [musicVideoTitle, setMusicVideoTitle] = useState('');

  // Add this block - Missing pendingVideoRef declaration
  const pendingVideoRef = useRef(null);

  const [games, setGames] = useState(() => loadGames());

  const [publishGame, setPublishGame] = useState(null);
  const [shareGame, setShareGame] = useState(null);
  const [deleteGame, setDeleteGame] = useState(null);
  const [editingGame, setEditingGame] = useState(null);
  const [toast, setToast] = useState(null);

  // New feature states
  const [latestCreation, setLatestCreation] = useState(null);
  const [showPreviewPanel, setShowPreviewPanel] = useState(false);
  const [showPostSuggestions, setShowPostSuggestions] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assetGenOpen, setAssetGenOpen] = useState(false);
  const [trailerGame, setTrailerGame] = useState(null);
  const [publishPlatformGame, setPublishPlatformGame] = useState(null);

  const { user, isAuthenticated, isPlatformOwner, isSuperAdmin, simulatedTier, effectiveTier } = useAuth();
  const [userSubscription, setUserSubscription] = useState(null);
  const [userCoins, setUserCoins] = useState(0);

  const [rightTab, setRightTab] = useState('library'); // 'library' | 'creations'

  const isSafe = !prompt?.trim() || checkSafety(prompt);
  const progressRef = useRef(null);
  const abortRef = useRef(false);
  const previewRef = useRef(null);

  // Persist games to localStorage
  useEffect(() => {
    saveGames(games);
  }, [games]);

  // Show toast notification
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // AI Generation using OpenAI or fallback simulation
  const generateWithAI = useCallback(async (userPrompt, gameMode, gameComplexity) => {
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    const geminiKey = import.meta.env?.VITE_GEMINI_API_KEY;

    if (apiKey && apiKey !== 'your-openai-api-key-here') {
      try {
        const systemPrompt = `You are an AI creation assistant for LoopNest. Generate a creative title and brief description based on the user's prompt. Mode: ${gameMode}. Complexity: ${gameComplexity}. Respond with JSON: {"title": "...", "description": "...", "genre": "...", "difficulty": "Easy|Medium|Hard"}`;
        const response = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
          body: JSON.stringify({
            model: 'gpt-3.5-turbo',
            messages: [{ role: 'system', content: systemPrompt }, { role: 'user', content: userPrompt }],
            max_tokens: 200,
            temperature: 0.8
          })
        });
        if (response?.ok) {
          const data = await response?.json();
          const content = data?.choices?.[0]?.message?.content;
          const parsed = JSON.parse(content);
          return { title: parsed?.title, description: parsed?.description, genre: parsed?.genre, difficulty: parsed?.difficulty };
        }
      } catch {}
    }

    if (geminiKey && geminiKey !== 'your-gemini-api-key-here') {
      try {
        const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=${geminiKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: `Generate a creative title and description for: "${userPrompt}". Mode: ${gameMode}. Return JSON: {"title": "...", "description": ".."}` }] }]
          })
        });
        if (response?.ok) {
          const data = await response?.json();
          const text = data?.candidates?.[0]?.content?.parts?.[0]?.text;
          const match = text?.match(/\{[\s\S]*\}/);
          if (match) {
            const parsed = JSON.parse(match?.[0]);
            return { title: parsed?.title, description: parsed?.description };
          }
        }
      } catch {}
    }

    const words = userPrompt?.trim()?.split(' ')?.filter(Boolean);
    const titleWords = words?.slice(0, 4)?.map((w) => w?.charAt(0)?.toUpperCase() + w?.slice(1));
    return {
      title: titleWords?.join(' ') || 'My New Creation',
      description: userPrompt?.slice(0, 100),
      genre: gameMode === 'text-to-game' ? 'Platformer' : gameMode === 'prompt-to-mini-game' ? 'Arcade' : 'Creative',
      difficulty: gameComplexity === 'simple' ? 'Easy' : gameComplexity === 'complex' ? 'Hard' : 'Medium'
    };
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!prompt?.trim() || !isSafe || isGenerating) return;

    // Check game quota before starting game generation
    if ((mode === 'text-to-game' || mode === 'prompt-to-mini-game') && user?.id && !isPlatformOwner) {
      const gameQuotaCheck = await checkGameQuota(user?.id, effectiveTier || 'free', user?.email || '');
      if (!gameQuotaCheck?.allowed) {
        showToast(gameQuotaCheck?.message || 'Game generation quota exceeded. Upgrade your plan.', 'error');
        setShowUpgradeModal(true);
        return;
      }
    }

    setIsGenerating(true);
    setGenProgress(0);
    setGenStatus('generating');
    setGenError(null);
    setShowPreviewPanel(false);
    setShowPostSuggestions(false);
    setLatestCreation(null);
    abortRef.current = false;

    const duration = complexity === 'simple' ? 4000 : complexity === 'medium' ? 8000 : 14000;
    const interval = 150;
    const increment = interval / duration * 85;
    let current = 0;

    progressRef.current = setInterval(() => {
      if (abortRef?.current) return;
      current += increment;
      if (current >= 85) {
        current = 85;
        clearInterval(progressRef?.current);
      }
      setGenProgress(current);
    }, interval);

    try {
      const aiResult = await generateWithAI(prompt, mode, complexity);

      if (abortRef?.current) return;

      clearInterval(progressRef?.current);
      setGenProgress(100);

      const thumbnail = GAME_THUMBNAILS?.[Math.floor(Math.random() * GAME_THUMBNAILS?.length)];
      const newGame = {
        id: Date.now(),
        title: aiResult?.title || prompt?.slice(0, 40),
        prompt,
        mode,
        complexity,
        thumbnail,
        thumbnailAlt: `AI generated content for: ${aiResult?.title || prompt?.slice(0, 30)}`,
        createdAt: new Date()?.toLocaleDateString('en-US'),
        views: 0,
        published: false,
        rating: 'E',
        description: aiResult?.description || '',
        genre: aiResult?.genre || 'Creative',
        difficulty: aiResult?.difficulty || 'Medium',
        ...(mode === 'music-to-video' && {
          musicSyncMode,
          musicOutputType,
          hasLyrics: !!lyricsText?.trim(),
          videoId: pendingVideoRef?.current?.videoId || null,
          videoUrl: pendingVideoRef?.current?.videoUrl || null,
          audioUrl: pendingVideoRef?.current?.audioUrl || null,
          mimeType: pendingVideoRef?.current?.mimeType || 'video/webm',
          bpm: pendingVideoRef?.current?.bpm || null,
          waveform: pendingVideoRef?.current?.waveform || null,
          duration: pendingVideoRef?.current?.duration || null,
          visualStyle: pendingVideoRef?.current?.visualStyle || null,
          captionMode: pendingVideoRef?.current?.captionMode || null,
          durationMode: pendingVideoRef?.current?.durationMode || null,
          sceneTimeline: pendingVideoRef?.current?.sceneTimeline || [],
          testMode
        })
      };

      setGames((prev) => [newGame, ...prev]);
      setLatestCreation(newGame);
      setShowPreviewPanel(true);
      setShowPostSuggestions(true);
      setIsGenerating(false);
      setGenStatus('success');
      setPrompt('');
      setEditingGame(null);
      if (mode === 'music-to-video') {
        setMusicFile(null);
        setLyricsText('');
        setMusicVideoTitle('');
        pendingVideoRef.current = null;
      }
      showToast(`"${newGame?.title}" created successfully!`);

      setTimeout(() => {
        previewRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 300);
    } catch (err) {
      if (!abortRef?.current) {
        clearInterval(progressRef?.current);
        setIsGenerating(false);
        setGenStatus('error');
        setGenError('Generation failed. Please try again.');
        showToast('Generation failed. Please try again.', 'error');
      }
    }
  }, [prompt, isSafe, isGenerating, complexity, mode, generateWithAI, showToast, musicSyncMode, musicOutputType, lyricsText]);

  const handleCancel = () => {
    abortRef.current = true;
    if (progressRef?.current) clearInterval(progressRef?.current);
    setIsGenerating(false);
    setGenProgress(0);
    setGenStatus('idle');
    showToast('Generation cancelled', 'info');
  };

  useEffect(() => () => {
    if (progressRef?.current) clearInterval(progressRef?.current);
  }, []);

  const handlePlay = (game) => {
    const gameMode = game?.mode || '';
    if (gameMode === 'text-to-game' || gameMode === 'prompt-to-mini-game') {
      navigate('/game-player-screen', { state: { game } });
    } else if (gameMode === 'script-to-video') {
      navigate(`/video/${game?.id}`, { state: { item: game } });
    } else if (gameMode === 'music-to-video') {
      navigate(`/music-video/${game?.id}`, { state: { item: game } });
    } else if (gameMode === 'text-to-animation') {
      navigate(`/animation/${game?.id}`, { state: { item: game } });
    } else {
      navigate('/game-player-screen', { state: { game } });
    }
  };

  const handleEdit = (game) => {
    setEditingGame(game);
    setPrompt(game?.prompt);
    setMode(game?.mode);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    showToast('Editing prompt — make changes and regenerate', 'info');
  };

  const handleRemix = (game) => {
    setPrompt(game?.prompt || '');
    setMode(game?.mode || 'text-to-game');
    setComplexity(game?.complexity || 'medium');
    setEditingGame(null);
    setShowPreviewPanel(false);
    setShowPostSuggestions(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    showToast(`Remixing "${game?.title}" — modify and regenerate!`, 'info');
  };

  const handleDuplicate = (game) => {
    const dup = {
      ...game,
      id: Date.now(),
      title: `${game?.title} (Copy)`,
      published: false,
      views: 0,
      createdAt: new Date()?.toLocaleDateString('en-US')
    };
    setGames((prev) => [dup, ...prev]);
    showToast(`"${game?.title}" duplicated!`);
  };

  const handlePublish = (game) => setPublishGame(game);

  const handlePublishConfirm = (updatedGame) => {
    setGames((prev) => prev?.map((g) => g?.id === updatedGame?.id ? updatedGame : g));
    showToast(updatedGame?.published ? `"${updatedGame?.title}" published!` : `"${updatedGame?.title}" unpublished`);
  };

  const handleShare = (game) => setShareGame(game);
  const handleDelete = (game) => setDeleteGame(game);

  const handleDeleteConfirm = (game) => {
    setGames((prev) => prev?.filter((g) => g?.id !== game?.id));
    showToast(`"${game?.title}" deleted`, 'info');
  };

  const handleTogglePublish = (game) => {
    const updated = { ...game, published: !game?.published };
    setGames((prev) => prev?.map((g) => g?.id === updated?.id ? updated : g));
    showToast(updated?.published ? `"${game?.title}" published!` : `"${game?.title}" unpublished`);
  };

  const handleApplyAssistantSuggestion = (suggestion) => {
    if (latestCreation) {
      setPrompt(`${latestCreation?.prompt} — ${suggestion}`);
      showToast('AI suggestion applied! Click Generate to update.', 'info');
    }
  };

  const handleGenerateMultiple = (storyPrompt, outputTypes) => {
    showToast(`Generated ${outputTypes?.length} outputs from your story!`);
  };

  const handlePostSuggestionAction = (action, creation) => {
    if (action === 'trailer') setTrailerGame(creation);else
    if (action === 'publish') setPublishPlatformGame(creation);else
    if (action === 'music') {setMode('text-to-music');window.scrollTo({ top: 0, behavior: 'smooth' });} else
    if (action === 'game') {setMode('text-to-game');setPrompt(`Game based on: ${creation?.title}`);window.scrollTo({ top: 0, behavior: 'smooth' });} else
    if (action === 'video') {setMode('script-to-video');setPrompt(`Video for: ${creation?.title}`);window.scrollTo({ top: 0, behavior: 'smooth' });} else
    if (action === 'social') showToast('Social clip feature coming soon!', 'info');else
    if (action === 'thumbnail') showToast('Thumbnail generator coming soon!', 'info');
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setShowPreviewPanel(false);
    setShowPostSuggestions(false);
    if (newMode !== 'music-to-video') {
      setMusicFile(null);
      setLyricsText('');
    }
    if (newMode !== 'script-to-video' && newMode !== 'text-to-animation') {
      setUploadedImage(null);
    }
  };

  const handleSelectPlan = (planId) => {
    setUserTier(planId);
    setShowUpgradeModal(false);
    showToast(`Upgraded to ${planId?.charAt(0)?.toUpperCase() + planId?.slice(1)} plan!`);
  };

  const handleNavigate = (path) => navigate(path);

  const isVideoMode = mode === 'script-to-video' || mode === 'text-to-animation' || mode === 'music-to-video';
  const isSpecialMode = mode === 'lip-sync' || mode === 'image-to-video';

  const PANEL_HEADER = {
    'text-to-game': { icon: 'Gamepad2', title: 'Create New Game', subtitle: 'Describe your idea and let AI do the rest' },
    'prompt-to-mini-game': { icon: 'Zap', title: 'Create Mini Game', subtitle: 'Describe a quick arcade or puzzle game' },
    'text-to-animation': { icon: 'Film', title: 'Create Animation', subtitle: 'Describe an animated scene to generate' },
    'script-to-video': { icon: 'Video', title: 'Create Video', subtitle: 'Paste your script or describe the scene' },
    'music-to-video': { icon: 'Music', title: 'Create Music Video', subtitle: 'Upload music and generate synchronized visuals' },
    'text-to-music': { icon: 'Music2', title: 'Generate Music', subtitle: 'Create original AI music in any style' },
    'lip-sync': { icon: 'Mic', title: 'Lip Sync Video', subtitle: 'Animate a character with voice' },
    'image-to-video': { icon: 'ImagePlay', title: 'Image to Video', subtitle: 'Animate a single image with cinematic effects' }
  };

  const panelHeader = PANEL_HEADER?.[mode] || PANEL_HEADER?.['text-to-game'];

  const MODE_COLORS = {
    'text-to-game': 'var(--color-primary)',
    'prompt-to-mini-game': 'var(--color-secondary)',
    'text-to-animation': 'var(--color-accent)',
    'script-to-video': '#10B981',
    'music-to-video': '#F43F5E',
    'text-to-music': '#8B5CF6',
    'lip-sync': '#EC4899',
    'image-to-video': '#06B6D4'
  };
  const modeColor = MODE_COLORS?.[mode] || 'var(--color-primary)';

  // Load real subscription tier on mount
  useEffect(() => {
    if (!user?.id) return;
    // Super admin always gets enterprise; others load from DB
    if (isSuperAdmin || isPlatformOwner) {
      setUserTier(simulatedTier || 'enterprise');
      setTestMode(true);
      return;
    }
    getUserSubscription(user?.id)?.then((sub) => {
      if (sub?.tier) setUserTier(sub?.tier);
    });
  }, [user?.id, isSuperAdmin, isPlatformOwner, simulatedTier]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryTabNavigation />
      <ContentStateIndicator
        status={genStatus}
        progress={genProgress}
        label={
        genStatus === 'generating' ? 'Generating your content with AI...' :
        genStatus === 'success' ? 'Creation complete!' :
        genStatus === 'error' ? genError || 'Generation failed' : undefined
        }
        estimatedTime={complexity === 'simple' ? 4 : complexity === 'medium' ? 8 : 14}
        onDismiss={() => setGenStatus('idle')} />
      
      {/* Toast notification */}
      {toast &&
      <div
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in-up"
        style={{
          background: toast?.type === 'error' ? 'rgba(239,68,68,0.95)' : toast?.type === 'info' ? 'rgba(99,102,241,0.95)' : 'rgba(16,185,129,0.95)',
          color: 'white',
          backdropFilter: 'blur(8px)',
          boxShadow: 'var(--shadow-xl)'
        }}>
        
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : toast?.type === 'info' ? 'Info' : 'CheckCircle2'} size={16} color="white" />
          <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-body)' }}>{toast?.message}</span>
        </div>
      }
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">

          {/* Page header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
                Creator Studio
              </h1>
              <p className="text-sm md:text-base mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                Build games, videos, animations, music, and lip-sync videos with AI
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => navigate('/music-to-video')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                style={{ background: 'rgba(139,92,246,0.15)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.3)', fontFamily: 'var(--font-caption)' }}>
                
                <Icon name="Film" size={13} color="#8B5CF6" />
                Music → Video
              </button>
              <button
                onClick={() => navigate('/creator/analytics')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)', fontFamily: 'var(--font-caption)' }}>
                
                <Icon name="BarChart3" size={13} color="#F59E0B" />
                Analytics
              </button>
              <button
                onClick={() => navigate('/creator/referrals')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', border: '1px solid rgba(16,185,129,0.3)', fontFamily: 'var(--font-caption)' }}>
                
                <Icon name="Users" size={13} color="#10B981" />
                Referrals
              </button>
              <SubscriptionTierBadge tier={userTier} onUpgrade={() => setShowUpgradeModal(true)} compact />
              <Button
                variant="outline"
                iconName="Grid3X3"
                iconPosition="left"
                onClick={() => setShowToolGallery((v) => !v)}>
                
                Tools
              </Button>
              <Button
                variant="outline"
                iconName="Compass"
                iconPosition="left"
                onClick={() => navigate('/discover')}>
                
                Discover
              </Button>
              <Button
                variant="outline"
                iconName="Library"
                iconPosition="left"
                onClick={() => navigate('/library')}>
                
                Library
              </Button>
              {/* Test Mode toggle */}
              <button
                onClick={() => {setTestMode((v) => !v);showToast(testMode ? 'Test mode disabled' : 'Test mode enabled — premium features unlocked', 'info');}}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all hover:opacity-80"
                style={{
                  background: testMode ? 'rgba(245,158,11,0.15)' : 'var(--color-muted)',
                  color: testMode ? '#F59E0B' : 'var(--color-muted-foreground)',
                  border: `1px solid ${testMode ? 'rgba(245,158,11,0.4)' : 'var(--color-border)'}`,
                  fontFamily: 'var(--font-caption)'
                }}
                title="Toggle test mode for premium feature preview">
                
                <Icon name="FlaskConical" size={11} color={testMode ? '#F59E0B' : 'var(--color-muted-foreground)'} />
                {testMode ? 'TEST' : 'Test'}
              </button>
            </div>
          </div>

          {/* Tool Gallery (collapsible) */}
          {showToolGallery &&
          <div className="mb-6 animate-fade-in-up">
              <div
              className="rounded-2xl p-4"
              style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
              
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon name="Grid3X3" size={16} color="var(--color-primary)" />
                    <h2 className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>AI Tool Gallery</h2>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(99,102,241,0.1)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}>9 Tools</span>
                  </div>
                  <button onClick={() => setShowToolGallery(false)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all">
                    <Icon name="X" size={14} color="var(--color-muted-foreground)" />
                  </button>
                </div>
                <ToolGallery
                selectedTool={mode}
                onSelectTool={(toolId) => {
                  handleModeChange(toolId);
                  setShowToolGallery(false);
                  showToast(`Switched to ${toolId?.replace(/-/g, ' ')} mode`, 'info');
                }} />
              
              </div>
            </div>
          }

          {/* Stats */}
          <div className="mb-6">
            <StatsBar games={games} />
          </div>

          {/* Creator Analytics */}
          <div className="mb-6">
            <CreatorAnalytics games={games} />
          </div>

          {/* Achievements */}
          <div className="mb-6">
            <CreatorAchievements games={games} />
          </div>

          {/* Main layout */}
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">

            {/* Left: Creation Panel */}
            <div className="w-full lg:w-[520px] xl:w-[560px] flex-shrink-0">
              <div
                className="rounded-2xl p-5 md:p-6 flex flex-col gap-5 sticky top-20"
                style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
                
                {/* Panel header */}
                <div className="flex items-center gap-2">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300"
                    style={{ background: `linear-gradient(135deg, ${modeColor}, ${modeColor}cc)` }}>
                    
                    <Icon name={panelHeader?.icon} size={18} color="white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h2 className="text-base font-bold transition-all duration-200" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
                      {editingGame ? 'Edit Prompt' : panelHeader?.title}
                    </h2>
                    <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                      {editingGame ? `Editing: ${editingGame?.title}` : panelHeader?.subtitle}
                    </p>
                  </div>
                  {editingGame &&
                  <button
                    onClick={() => {setEditingGame(null);setPrompt('');}}
                    className="ml-auto w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all"
                    title="Cancel editing">
                    
                      <Icon name="X" size={14} color="var(--color-muted-foreground)" />
                    </button>
                  }
                </div>

                {/* Mode selector */}
                <div>
                  <div className="flex items-center justify-between mb-2.5">
                    <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
                      Creation Mode
                    </p>
                    <button
                      onClick={() => setShowToolGallery((v) => !v)}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition-all hover:opacity-80"
                      style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                      
                      <Icon name="Grid3X3" size={11} />
                      All Tools
                    </button>
                  </div>
                  <ModeSelector selectedMode={mode} onModeChange={handleModeChange} disabled={isGenerating} />
                </div>

                {/* Lip Sync mode */}
                {mode === 'lip-sync' ?
                <div className="animate-fade-in-up">
                    <LipSyncWorkspace
                    onGenerated={(result) => {
                      showToast(`"${result?.title}" generated successfully!`);
                      setLatestCreation(result);
                      setShowPostSuggestions(true);
                    }}
                    disabled={isGenerating} />
                  
                  </div> :
                mode === 'image-to-video' ?
                <div className="animate-fade-in-up">
                    <ImageToVideoWorkspace
                    onGenerated={(result) => {
                      showToast(`"${result?.title}" generated successfully!`);
                      setLatestCreation(result);
                      setShowPostSuggestions(true);
                    }}
                    disabled={isGenerating} />
                  
                  </div> :
                mode === 'text-to-music' ?
                <div className="animate-fade-in-up">
                    <TextToMusicGenerator
                    onMusicGenerated={(music) => {
                      showToast(`"${music?.title}" composed successfully!`);
                      setLatestCreation(music);
                      setShowPostSuggestions(true);
                    }}
                    disabled={isGenerating} />
                  
                  </div> :

                <>
                    {/* Template System */}
                    <TemplateSystem
                    mode={mode}
                    onSelectTemplate={(tplPrompt) => {setPrompt(tplPrompt);showToast('Template loaded!', 'info');}}
                    disabled={isGenerating} />
                  

                    {/* Prompt input */}
                    <div>
                      <p className="text-sm font-semibold mb-2.5" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
                        Your Prompt
                      </p>
                      <PromptInput
                      mode={mode}
                      prompt={prompt}
                      onPromptChange={setPrompt}
                      onGenerate={handleGenerate}
                      isGenerating={isGenerating}
                      isSafe={isSafe} />
                    
                      {/* Prompt Enhancer */}
                      <div className="mt-2">
                        <PromptEnhancer
                        prompt={prompt}
                        mode={mode}
                        onEnhancedPrompt={(enhanced) => {
                          setPrompt(enhanced);
                          showToast('Prompt enhanced!', 'info');
                        }}
                        disabled={isGenerating} />
                      
                      </div>
                    </div>

                    {/* Image Upload — for video/animation modes */}
                    {(mode === 'script-to-video' || mode === 'text-to-animation') &&
                  <div className="animate-fade-in-up">
                        <ImageUploadWithSafety
                      onImageAccepted={(file) => {
                        setUploadedImage(file);
                        showToast('Image approved and ready to use!', 'info');
                      }}
                      disabled={isGenerating} />
                    
                      </div>
                  }

                    {/* Music Upload — only for music-to-video mode */}
                    {mode === 'music-to-video' &&
                  <div className="animate-fade-in-up">
                        <MusicUpload
                      musicFile={musicFile}
                      onMusicFileChange={setMusicFile}
                      syncMode={musicSyncMode}
                      onSyncModeChange={setMusicSyncMode}
                      outputType={musicOutputType}
                      onOutputTypeChange={setMusicOutputType}
                      lyricsText={lyricsText}
                      onLyricsTextChange={setLyricsText}
                      disabled={isGenerating}
                      testMode={testMode}
                      userTier={userTier}
                      onVideoGenerated={(result) => {
                        pendingVideoRef.current = result;
                        setMusicVideoTitle(result?.title || '');
                      }} />
                    
                      </div>
                  }

                    {/* Settings */}
                    <div>
                      <p className="text-sm font-semibold mb-2.5" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
                        Generation Settings
                      </p>
                      <GenerationSettings
                      complexity={complexity}
                      onComplexityChange={setComplexity}
                      safeMode={safeMode}
                      onSafeModeChange={setSafeMode}
                      disabled={isGenerating}
                      tier={userTier}
                      onUpgrade={() => setShowUpgradeModal(true)} />
                    
                    </div>

                    {/* Subscription tier info */}
                    <SubscriptionTierBadge tier={userTier} onUpgrade={() => setShowUpgradeModal(true)} />

                    {/* Progress */}
                    <GenerationProgress
                    isGenerating={isGenerating}
                    progress={genProgress}
                    mode={mode}
                    onCancel={handleCancel} />
                  

                    {/* Output Preview Panel — shown after generation */}
                    {showPreviewPanel && latestCreation &&
                  <div ref={previewRef} className="animate-fade-in-up">
                        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
                          ✨ Your Creation is Ready
                        </p>
                        <OutputPreviewPanel
                      game={latestCreation}
                      onPlay={handlePlay}
                      onShare={() => setShareGame(latestCreation)}
                      onRemix={handleRemix}
                      onClose={() => setShowPreviewPanel(false)} />
                    
                        {/* Quick action buttons */}
                        <div className="flex gap-2 mt-2">
                          {isVideoMode &&
                      <button
                        onClick={() => {setEditingVideoGame(latestCreation);setShowVideoEditor(true);}}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border transition-all hover:opacity-80"
                        style={{ borderColor: 'rgba(139,92,246,0.4)', background: 'rgba(139,92,246,0.08)', color: '#8B5CF6', fontFamily: 'var(--font-caption)' }}>
                        
                              <Icon name="Scissors" size={12} color="#8B5CF6" />
                              Edit Video
                            </button>
                      }
                          <button
                        onClick={() => setTrailerGame(latestCreation)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border transition-all hover:opacity-80"
                        style={{ borderColor: 'rgba(99,102,241,0.4)', background: 'rgba(99,102,241,0.08)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}>
                        
                            <Icon name="Clapperboard" size={12} color="var(--color-primary)" />
                            Auto Trailer
                          </button>
                          <button
                        onClick={() => setMonetizeGame(latestCreation)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border transition-all hover:opacity-80"
                        style={{ borderColor: 'rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.08)', color: '#F59E0B', fontFamily: 'var(--font-caption)' }}>
                        
                            <Icon name="DollarSign" size={12} color="#F59E0B" />
                            Monetize
                          </button>
                          <button
                        onClick={() => setPublishPlatformGame(latestCreation)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border transition-all hover:opacity-80"
                        style={{ borderColor: 'rgba(16,185,129,0.4)', background: 'rgba(16,185,129,0.08)', color: 'var(--color-success)', fontFamily: 'var(--font-caption)' }}>
                        
                            <Icon name="Send" size={12} color="var(--color-success)" />
                            Publish
                          </button>
                        </div>
                      </div>
                  }

                    {/* Post-Generation Suggestions */}
                    {showPostSuggestions && latestCreation &&
                  <div className="animate-fade-in-up">
                        <PostGenerationSuggestions
                      creation={latestCreation}
                      onAction={handlePostSuggestionAction}
                      onDismiss={() => setShowPostSuggestions(false)} />
                    
                      </div>
                  }

                    {/* AI Creator Assistant */}
                    <AICreatorAssistant
                    currentGame={latestCreation}
                    onApplySuggestion={handleApplyAssistantSuggestion}
                    isOpen={assistantOpen}
                    onToggle={() => setAssistantOpen((v) => !v)} />
                  

                    {/* AI Asset Generator */}
                    <AIAssetGenerator
                    isOpen={assetGenOpen}
                    onToggle={() => setAssetGenOpen((v) => !v)} />
                  

                    {/* Story Multi-Output */}
                    <StoryMultiOutput
                    onGenerateMultiple={handleGenerateMultiple}
                    isGenerating={isGenerating}
                    disabled={isGenerating} />
                  
                  </>
                }

                {/* Copyright safety notice */}
                <div
                  className="flex items-start gap-2 p-3 rounded-xl"
                  style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)' }}>
                  
                  <Icon name="ShieldCheck" size={15} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-heading)' }}>Legal-Safe Content Generation</p>
                    <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                      All content is automatically screened. No copyrighted music, images, or video scenes are replicated. LoopNest generates 100% original AI content.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Games Library + My Creations */}
            <div className="flex-1 min-w-0">
              {/* Compute Wallet Dashboard */}
              <div className="mb-6">
                <ComputeWalletWidget showTopUp={true} />
              </div>

              {/* Right panel tab switcher */}
              <div className="flex gap-1 p-1 rounded-xl mb-4" style={{ background: 'var(--color-muted)' }}>
                {[
                  { id: 'library', label: 'My Games', icon: 'Gamepad2' },
                  { id: 'creations', label: 'My Creations', icon: 'Layers' },
                ]?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setRightTab(tab?.id)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all"
                    style={{
                      background: rightTab === tab?.id ? 'var(--color-card)' : 'transparent',
                      color: rightTab === tab?.id ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                      fontFamily: 'var(--font-heading)',
                      boxShadow: rightTab === tab?.id ? 'var(--shadow-sm)' : 'none',
                    }}
                  >
                    <Icon name={tab?.icon} size={14} color={rightTab === tab?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                    {tab?.label}
                  </button>
                ))}
              </div>

              {rightTab === 'library' && (
                <GamesLibrary
                  games={games}
                  onPlay={handlePlay}
                  onEdit={handleEdit}
                  onDuplicate={handleDuplicate}
                  onPublish={handlePublish}
                  onTogglePublish={handleTogglePublish}
                  onShare={handleShare}
                  onDelete={handleDelete}
                  onRemix={handleRemix}
                  onTrailer={setTrailerGame}
                  onPublishPlatform={setPublishPlatformGame}
                  onMonetize={setMonetizeGame}
                  onEditVideo={(game) => {setEditingVideoGame(game);setShowVideoEditor(true);}}
                />
              )}

              {rightTab === 'creations' && (
                <div className="rounded-2xl p-5" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
                  <MyCreationsTab />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      {/* Modals */}
      {publishGame &&
      <PublishModal
        game={publishGame}
        onClose={() => setPublishGame(null)}
        onPublish={handlePublishConfirm} />

      }
      {shareGame &&
      <ShareModal
        game={shareGame}
        onClose={() => setShareGame(null)} />

      }
      {deleteGame &&
      <DeleteConfirmModal
        game={deleteGame}
        onClose={() => setDeleteGame(null)}
        onConfirm={handleDeleteConfirm} />

      }
      {trailerGame &&
      <AutoTrailerGenerator
        game={trailerGame}
        onClose={() => setTrailerGame(null)} />

      }
      {publishPlatformGame &&
      <OneClickPublish
        game={publishPlatformGame}
        onClose={() => setPublishPlatformGame(null)} />

      }
      {showUpgradeModal &&
      <UpgradeModal
        currentTier={userTier}
        onClose={() => setShowUpgradeModal(false)}
        onSelectPlan={handleSelectPlan} />

      }
      {showVideoEditor && editingVideoGame &&
      <VideoEditor
        game={editingVideoGame}
        onClose={() => {setShowVideoEditor(false);setEditingVideoGame(null);}}
        onExport={(exported) => {
          showToast(`"${exported?.title}" exported successfully!`);
          setShowVideoEditor(false);
          setEditingVideoGame(null);
        }} />

      }
      {monetizeGame &&
      <MonetizationPanel
        game={monetizeGame}
        onClose={() => setMonetizeGame(null)} />

      }
    </div>);

}